"use client"

import { Button, FilterButton, IconButton } from "@/elements"
import { useCallback, useMemo, useRef } from "react"
import clsx from "clsx"
import useOutsideClick from "@/hooks/useOutsideClick"
import { JobPostingPayType } from "@/gql/graphql"
import { useFilters } from "@/components/Filters/useFilters"
import { FilterItem } from "./FilterItem"
import { XMarkIcon } from "@heroicons/react/24/outline"

interface Props {
  onSelect?: (name: string) => void
  menuPosition?: "left" | "right"
  onClickOverride?: () => { hasSubscription: boolean }
}

const id = "workType"

export const WorkTypeFilter: React.FC<Props> = ({ onSelect, menuPosition = "right", onClickOverride }) => {
  const menuRef = useRef<HTMLDivElement>(null)
  const { currentOpenMenu, onToggleMenu, onCloseMenu, toggleFilter, getActiveFilters } = useFilters()

  const selected = getActiveFilters(id)

  const isPaidSelected = useMemo(() => {
    if (selected.length === 0) {
      return true
    }

    return selected.includes("paid")
  }, [selected])

  const isCollaborativeSelected = useMemo(() => {
    return selected.includes("collaborative")
  }, [selected])

  const isAllSelected = useMemo(() => {
    return selected.includes("all")
  }, [selected])

  useOutsideClick(id, () => {
    if (currentOpenMenu === id) {
      onCloseMenu()
    }
  })

  const filters = useMemo(
    () => [
      {
        name: "Standard paid",
        onClick: () => {
          toggleFilter({
            params: [
              {
                key: id,
                values: ["paid"],
              },
            ],
            multiSelect: false,
          })
          onSelect?.("paid")
        },
        isSelected: isPaidSelected,
      },
      {
        name: "Collaborative",
        onClick: () => {
          toggleFilter({
            params: [
              {
                key: id,
                values: ["collaborative"],
              },
            ],
            multiSelect: false,
          })
          onSelect?.("collaborative")
        },
        isSelected: isCollaborativeSelected,
      },
      {
        name: "All",
        onClick: () => {
          toggleFilter({
            params: [
              {
                key: id,
                values: ["all"],
              },
            ],
            multiSelect: false,
          })
          onSelect?.("all")
        },
        isSelected: isAllSelected,
      },
    ],
    [isPaidSelected, isCollaborativeSelected, isAllSelected, toggleFilter]
  )

  const isOpen = currentOpenMenu === id

  const onClickMenuButton = () => {
    if (onClickOverride) {
      const { hasSubscription } = onClickOverride()
      if (hasSubscription) {
        onToggleMenu(id)
      }
    } else {
      onToggleMenu(id)
    }
  }

  const buttonDisplayText = useMemo(() => {
    if (isAllSelected) {
      return "All"
    } else if (isCollaborativeSelected) {
      return "Collaborative"
    }
    return "Paid"
  }, [isAllSelected, isCollaborativeSelected])

  return (
    <div className="relative" ref={menuRef} id={id}>
      <FilterButton
        label={buttonDisplayText}
        onClick={onClickMenuButton}
        active={isAllSelected || isCollaborativeSelected}
        open={isOpen}
        showXIcon={false}
      />
      <div
        className={clsx(
          "duration-250 fixed bottom-auto top-[72px] z-50 h-[calc(100dvh-72px)] w-full transition-opacity ease-out sm:absolute sm:top-auto sm:z-30 sm:h-fit sm:w-[320px]",
          isOpen ? "opacity-1 pointer-events-auto" : "pointer-events-none opacity-0",
          menuPosition === "right" ? "left-auto right-0" : "left-0 right-auto"
        )}>
        <div className="shadow-filter-button mt-0 h-full overflow-y-auto overscroll-contain border-none bg-bg-1 px-5 py-4 sm:mx-auto sm:mt-2 sm:h-auto sm:overflow-hidden sm:rounded-xl sm:border sm:border-solid sm:border-bg-3 sm:p-0">
          <div className="block sm:hidden">
            <div className="flex flex-row justify-end pb-1">
              <IconButton Icon={XMarkIcon} onClick={onCloseMenu} variant="white" />
            </div>
          </div>
          <div className="flex h-[calc(100%-92px)] flex-col gap-1 overflow-scroll px-2">
            <div className="my-3 flex w-full">
              <div className="flex w-full flex-col gap-1">
                {filters?.map(filter => {
                  return (
                    <FilterItem
                      key={filter.name}
                      name={filter.name}
                      selected={filter.isSelected}
                      onClick={filter.onClick}
                    />
                  )
                })}
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 flex h-16 w-full flex-row items-center justify-end border-t border-bg-2 bg-bg-1 px-5 sm:hidden">
            <Button onClick={onCloseMenu}>Done</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
